import emailSpellChecker from '@zootools/email-spell-checker';
import Personnummer from 'personnummer';
import { z } from 'zod';
import type { Country } from '@/constants/config';

export const isValidEmail = (value: string): boolean => z.email().safeParse(value).success;

export const nameRegexp = /^[a-zA-Z\s-]+$/; // Alphabetical with space and "-"

export const currencyRegexp = /^\d+$/; // Only integers

export const sortCodeRegex = /^\d{2}-\d{2}-\d{2}$/;

export const eightDigitRegexp = /^\d{8}$/;

// Source: https://ideal-postcodes.co.uk/guides/postcode-validation
export const postalCodesRegExp = {
  UK: /^[a-z]{1,2}\d[a-z\d]?\s*\d[a-z]{2}$/i,
};

export const mobileNumberRegexp: Record<Country, RegExp> = {
  SE: /^07([0-9][ -]*){7}[0-9]$/im,
  UK: /^\+44\d{10}$/,
  // TODO: FI - Add regex
  FI: /^/,
};

export const finnishNationalNumberRegex = /^\d{6}[-+A-Z]\d{3}[\dA-Z]$/;

export const isNumber = (value: unknown) => !Number.isNaN(Number(value));

export const isNationalNumberValid = (nationalNumber: string): boolean => {
  if (!Personnummer.valid(nationalNumber)) {
    return false;
  }

  return ['19', '20'].includes(new Personnummer(nationalNumber).century);
};

export const isNationalNumberBirthYearValid = (nationalNumber: string): boolean => {
  if (!Personnummer.valid(nationalNumber)) {
    return false;
  }

  const birthYear = Number(new Personnummer(nationalNumber).fullYear);
  return birthYear <= new Date().getFullYear();
};

/**
 * Suggests a corrected email address by checking against common email domains
 * and specific company domains (telia.com, bahnhof.se).
 *
 * @param email - The potentially misspelled email address to check
 * @returns The suggested corrected email address, or undefined if no suggestion is found
 *
 * @example
 * suggestCorrectEmail('user@gmial.com') // 'user@gmail.com'
 * suggestCorrectEmail('user@tellia.com') // 'user@telia.com'
 */
export const suggestCorrectEmail = (email: string) => {
  const suggestion = emailSpellChecker.run({
    email,
    domains: [...emailSpellChecker.POPULAR_DOMAINS, 'telia.com', 'bahnhof.se'],
  });

  return suggestion?.full;
};
